
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FileText, Upload, AlertTriangle, CheckCircle, XCircle, Info, FileX } from "lucide-react";
import { UploadButton } from "@/components/ui/upload-button";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

type DocumentStatus = 'pending' | 'accepted' | 'rejected' | 'not_uploaded';

interface DocumentItem {
  id: string;
  title: string;
  description: string;
  required: boolean;
  status: DocumentStatus;
  uploadDate?: string;
  feedback?: string;
  filePath?: string;
  fileName?: string;
}

const ProfessionalDocumentsPage = () => {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<DocumentItem[]>([
    {
      id: "medical-license",
      title: "Medical License / Professional Certificate",
      description: "Upload your valid medical license or professional practice certificate.",
      required: true,
      status: "not_uploaded"
    },
    {
      id: "degree",
      title: "University Degree / Diploma",
      description: "Upload your dental degree or equivalent qualification.",
      required: true,
      status: "not_uploaded"
    },
    {
      id: "identity",
      title: "Identity Document",
      description: "Upload a copy of your ID or passport.",
      required: true,
      status: "not_uploaded"
    },
    {
      id: "clinic-affiliation",
      title: "Proof of Clinic Affiliation",
      description: "Upload your employment contract or affiliation letter with your clinic.",
      required: true,
      status: "not_uploaded"
    },
    {
      id: "insurance",
      title: "Professional Liability Insurance",
      description: "Upload your current liability insurance documentation.",
      required: false,
      status: "not_uploaded"
    },
    {
      id: "additional",
      title: "Additional Requested Document",
      description: "Upload any additional document requested by your clinic or the admin.",
      required: false,
      status: "not_uploaded"
    }
  ]);

  // Check document verification status
  const allRequiredDocsUploaded = documents
    .filter(doc => doc.required)
    .every(doc => doc.status !== 'not_uploaded');
    
  const allDocsVerified = documents
    .filter(doc => doc.required)
    .every(doc => doc.status === 'accepted');

  // Handle file upload
  const handleFileUpload = (documentId: string, file: File) => {
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10MB",
        variant: "destructive"
      });
      return;
    }
    
    // Validate file type
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF, PNG, or JPG file",
        variant: "destructive"
      });
      return;
    }
    
    // In a real app, this would upload to a server
    // For now, we'll just update the state
    setDocuments(prevDocs => prevDocs.map(doc => {
      if (doc.id === documentId) {
        return {
          ...doc,
          status: 'pending',
          uploadDate: new Date().toISOString(),
          fileName: file.name,
          filePath: URL.createObjectURL(file) // For preview only
        };
      }
      return doc;
    }));
    
    toast({
      title: "Document uploaded",
      description: "Your document has been uploaded and is pending review",
    });
  };
  
  // Handle delete document
  const handleDeleteDocument = (documentId: string) => {
    setDocuments(prevDocs => prevDocs.map(doc => {
      if (doc.id === documentId) {
        return {
          ...doc,
          status: 'not_uploaded',
          uploadDate: undefined,
          fileName: undefined,
          filePath: undefined,
          feedback: undefined
        };
      }
      return doc;
    }));
    
    toast({
      title: "Document removed",
      description: "Your document has been removed",
    });
  };
  
  // For demo purposes, simulate verification
  const simulateVerification = (documentId: string, newStatus: 'accepted' | 'rejected') => {
    setDocuments(prevDocs => prevDocs.map(doc => {
      if (doc.id === documentId) {
        return {
          ...doc,
          status: newStatus,
          feedback: newStatus === 'rejected' 
            ? "Document is unclear or expired. Please upload a valid document."
            : "Document has been verified and accepted."
        };
      }
      return doc;
    }));
    
    toast({
      title: newStatus === 'accepted' ? "Document accepted" : "Document rejected",
      description: newStatus === 'accepted' 
        ? "Your document has been verified and accepted" 
        : "Please review admin feedback and reupload your document",
      variant: newStatus === 'accepted' ? "default" : "destructive",
    });
  };

  // Render status badge 
  const renderStatusBadge = (status: DocumentStatus) => {
    switch (status) {
      case 'accepted':
        return <Badge className="bg-green-500 hover:bg-green-600"><CheckCircle className="w-3 h-3 mr-1" /> Accepted</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      case 'pending':
        return <Badge variant="outline" className="text-amber-500 border-amber-500"><AlertTriangle className="w-3 h-3 mr-1" /> Pending Review</Badge>;
      case 'not_uploaded':
        return <Badge variant="outline" className="text-slate-500"><Upload className="w-3 h-3 mr-1" /> Not Uploaded</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex flex-col space-y-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Professional Documents</h1>
          <p className="text-muted-foreground">
            Upload the documents required to verify your professional status and clinic affiliation. 
            These documents are only visible to the platform administrator and your clinic.
          </p>
        </div>
        
        {/* Verification status alert */}
        {!allDocsVerified && (
          <Alert variant={allRequiredDocsUploaded ? "default" : "destructive"}>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {allRequiredDocsUploaded 
                ? "Documents pending verification" 
                : "Required documents missing"}
            </AlertTitle>
            <AlertDescription>
              {allRequiredDocsUploaded 
                ? "Your documents are uploaded and awaiting admin review. Some system features may be limited until verification is complete."
                : "Your account is restricted until all required professional documents are uploaded and verified by the platform admin."}
            </AlertDescription>
          </Alert>
        )}
        
        {/* Documents list */}
        <div className="grid grid-cols-1 gap-6">
          {documents.map((document) => (
            <Card key={document.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      {document.title}
                      {document.required && <span className="ml-2 text-xs text-red-500">*Required</span>}
                    </CardTitle>
                    <CardDescription className="mt-1">{document.description}</CardDescription>
                  </div>
                  {renderStatusBadge(document.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Document preview (if uploaded) */}
                  {document.status !== 'not_uploaded' && (
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <FileText className="h-5 w-5 mr-2 text-muted-foreground" />
                          <span className="text-sm font-medium">{document.fileName}</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Uploaded: {document.uploadDate ? new Date(document.uploadDate).toLocaleDateString() : ''}
                        </div>
                      </div>
                      
                      {/* Preview for image files - in a real app this would be more sophisticated */}
                      {document.filePath && document.fileName?.match(/\.(jpeg|jpg|png)$/i) && (
                        <div className="mt-2 border rounded p-2 bg-muted">
                          <img 
                            src={document.filePath} 
                            alt={document.title} 
                            className="max-h-40 object-contain mx-auto" 
                          />
                        </div>
                      )}
                      
                      {/* Preview for PDF files - just show a placeholder in this demo */}
                      {document.filePath && document.fileName?.endsWith('.pdf') && (
                        <div className="mt-2 border rounded p-4 bg-muted flex items-center justify-center">
                          <FileText className="h-12 w-12 text-primary opacity-70" />
                          <span className="ml-2 text-sm text-muted-foreground">PDF Document</span>
                        </div>
                      )}
                      
                      {/* Admin feedback */}
                      {document.feedback && (
                        <div className={`p-3 rounded-md text-sm ${
                          document.status === 'rejected' 
                            ? 'bg-red-50 text-red-700 border border-red-200' 
                            : 'bg-green-50 text-green-700 border border-green-200'
                        }`}>
                          <div className="flex items-start">
                            <Info className="h-4 w-4 mt-0.5 mr-2" />
                            <div>
                              <p className="font-medium mb-1">Admin Feedback:</p>
                              <p>{document.feedback}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex space-x-2">
                        {/* Re-upload option */}
                        <UploadButton
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(document.id, file);
                          }}
                        >
                          Re-upload
                        </UploadButton>
                        
                        {/* Delete document */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" className="text-red-500 border-red-200 hover:bg-red-50">
                              <FileX className="h-4 w-4 mr-2" />
                              Remove
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action will remove the document. You will need to upload it again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteDocument(document.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        
                        {/* Demo actions - for demonstration only */}
                        {document.status === 'pending' && (
                          <div className="ml-auto border rounded p-2 bg-gray-50">
                            <p className="text-xs text-muted-foreground mb-2">Demo: Simulate admin review</p>
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="bg-green-500 hover:bg-green-600 text-white"
                                onClick={() => simulateVerification(document.id, 'accepted')}
                              >
                                Accept
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="bg-red-500 hover:bg-red-600 text-white"
                                onClick={() => simulateVerification(document.id, 'rejected')}
                              >
                                Reject
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Upload button (if not uploaded) */}
                  {document.status === 'not_uploaded' && (
                    <div className="flex flex-col space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Accepted formats: PDF, PNG, JPG (Max size: 10MB)
                      </p>
                      <UploadButton
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(document.id, file);
                        }}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Document
                      </UploadButton>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="mt-8 border-t pt-6">
          <h3 className="text-lg font-medium mb-2">Document Guidelines</h3>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            <li>All documents must be clear, legible and not expired</li>
            <li>Documents are reviewed within 1-3 business days</li>
            <li>Your documents are securely stored and only visible to administrators</li>
            <li>If a document is rejected, please review the feedback and upload a new version</li>
            <li>For any issues with document verification, please contact support</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalDocumentsPage;
